import React, { useState, useEffect, useRef } from "react";
import locationMap from "../../../assets/images/map.png";
import ssidGraph from "../../../assets/images/ssid-graph.png";
import deviceImage from "../../../assets/images/device.png";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import switchIcon from "../../../assets/images/switch.svg";
import apIcon from "../../../assets/images/access-point.svg";
import "leaflet/dist/leaflet.css";
import Chart from "react-apexcharts";
import { useMap } from "react-leaflet";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend,
  ResponsiveContainer,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import allApi from "../../../api/allApi";
import Loader from "../common/Loader";
import StackedHorizontalChart from "../dashboard/StackedHorizontalChart";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useLocalization } from "../localization/LocalizationContext";

function MapView({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12); // Adjust the zoom level as needed
    }
  }, [center, map]);

  return null;
}

function DeviceDashboard(networkId) {
  const location = useLocation();
  const { localizationStrings } = useLocalization();
  const { deviceId } = location.state || {};
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [usageDetails, setUsageDetails] = useState({});
  const [dataUsage, setDataUsage] = useState([]);
  const [apDataUsage, setApDataUsage] = useState([]);
  const [apInterval, setApInterval] = useState("7d");
  const [selectedInterval, setSelectedInterval] = useState("7d");
  const [upDownData, setUpDownData] = useState([]);
  const [colorRanges, setColorRanges] = useState([]);
  const initialDeviceData = location.state?.deviceInfo || {};
  const [deviceType, setDeviceType] = useState(
    initialDeviceData.deviceType || "N/A"
  );
  const [connectedClients, setConnectedClients] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [deviceLocation, setDeviceLocation] = useState([51.505, -0.09]);
  const [xAxisLabels, setXAxisLabels] = useState([]);
  const [ConnectedClientSeries, setConnectedClientSeries] = useState([]);
  const [ConnectedClientCategories, setConnectedClientCategories] = useState(
    []
  );
  const [loading, setLoading] = useState(true);
  const [connectedClientsDuration, setConnectedClientsDuration] =
    useState("24h");

  useEffect(() => {
    getConnectedClientPerSSID(connectedClientsDuration);
  }, [connectedClientsDuration]);
  const getMarkerIcon = (deviceType) => {
    let iconUrl;

    if (deviceType === "AP") {
      iconUrl = apIcon;
    } else if (deviceType === "Switch") {
      iconUrl = switchIcon;
    } else {
      iconUrl = apIcon;
    }

    return L.icon({
      iconUrl: iconUrl,
      iconSize: [45, 45],
      iconAnchor: [12, 41],
    });
  };

  useEffect(() => {
    fetchDeviceDetails(deviceId);
    fetchCpuAndMemoryUsage(deviceId);
  }, [deviceId]);

  useEffect(() => {
    gethttpLocations(deviceId);
  }, [deviceId]);

  useEffect(() => {
    fetchDeviceUpDownData(deviceId);
  }, [deviceId]);

  useEffect(() => {
    if (deviceType === "AP") {
      DataUsageAP(deviceId, networkId);
      getConnectedClientPerSSID(connectedClientsDuration);
    } else if (deviceType === "SW" || deviceType === "Switch") {
      fetchConnectedClients(deviceId);
      handleDataUsage(deviceId);
    }
  }, [
    deviceType,
    deviceId,
    selectedInterval,
    apInterval,
    networkId,
    connectedClientsDuration,
  ]);

  const fetchDeviceDetails = async (deviceId) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/http/device/${deviceId}`);
      if (response) {
        setDeviceDetails(response);
        setDeviceType(response.deviceType);
      } else {
        console.error("No data returned from API");
      }
    } catch (error) {
      console.error("Error fetching device details:", error.message || error);
    } finally {
      setLoading(false);
    }
  };

  const gethttpLocations = async (deviceId) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/http/${deviceId}/location`);

      if (response && response.latitude && response.longitude) {
        const location = {
          id: response.deviceId,
          position: [
            parseFloat(response.latitude),
            parseFloat(response.longitude),
          ],
          icon: getMarkerIcon(),
        };
        setNodes([location]);
        setDeviceLocation([
          parseFloat(response.latitude),
          parseFloat(response.longitude),
        ]);
      } else {
        console.error("Unexpected API response structure:", response);
      }
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const fetchCpuAndMemoryUsage = async (deviceId) => {
    try {
      setLoading(true);
      const api = new allApi();
      const response = await api.getRequest(
        `api/wired/dashboard/memoryCpuUtilization/${deviceId}`
      );
      if (response) {
        setUsageDetails({
          cpuUsage: response.cpuUsage,
          memoryUsage: response.memoryUsage,
        });
      } else {
        console.error("Failed to fetch device details.");
      }
    } catch (error) {
      console.error("Error fetching CPU and memory usage:", error);
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateString, interval) => {
    if (["6h", "12h", "24h"].includes(interval)) {
      return moment.utc(dateString, "DD-MM-YYYY HH:mm").local().format();
    } else {
      return moment.utc(dateString, "DD-MM-YYYY").local().format();
    }
  };

  const formatXAxisLabel = (value, interval) => {
    if (["6h", "12h", "24h"].includes(interval)) {
      return moment(value).format("HH:mm");
    }
    return moment(value).format("DD MMM");
  };

  const handleDataUsage = async (deviceId, interval = selectedInterval) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wired/dashboard/dataUsage/${deviceId}/${interval}`
      );

      if (!Array.isArray(response) || response.length === 0) {
        setDataUsage([]);
        return;
      }

      const dataMap = {};

      // First sort response by date
      const sortedResponse = [...response].sort(
        (a, b) =>
          moment(a.created_at, "DD-MM-YYYY").valueOf() -
          moment(b.created_at, "DD-MM-YYYY").valueOf()
      );

      sortedResponse.forEach((item, index) => {
        const timestamp = moment(
          item.created_at,
          ["DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm", "DD-MM-YYYY"],
          true
        ).valueOf();

        if (!isNaN(timestamp)) {
          let dailyTraffic = 0;

          if (index === 0) {
            dailyTraffic = item.total_traffic;
          } else {
            const previous = sortedResponse[index - 1].total_traffic;

            // Handle reset case
            dailyTraffic =
              item.total_traffic >= previous
                ? item.total_traffic - previous
                : item.total_traffic;
          }

          dataMap[timestamp] = +(dailyTraffic / 1_000_000).toFixed(2);
        }
      });

      const isHourly = ["6h", "12h", "24h"].includes(interval);

      const startTime = moment(Math.min(...Object.keys(dataMap)));
      const endTime = moment(Math.max(...Object.keys(dataMap)));

      const normalizedData = [];
      let cursor = startTime.clone();

      while (cursor.isSameOrBefore(endTime)) {
        const ts = cursor.valueOf();

        normalizedData.push({
          date: ts,
          totalTraffic: dataMap[ts] || 0, // ✅ fill missing with 0
        });

        if (isHourly) {
          cursor.add(1, "hour");
        } else {
          cursor.add(1, "day");
        }
      }

      setDataUsage(normalizedData);
    } catch (error) {
      console.error("Error fetching data usage:", error);
      setDataUsage([]);
    } finally {
      setLoading(false);
    }
  };

  const DataUsageAP = async (deviceId, networkId) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/clientDataConsumption?duration=${apInterval}&networkId=6001&deviceId=${deviceId}`
      );

      if (response) {
        const formattedData = response.response.map((entry) => {
          return {
            serialNumber: entry.serialNumber,
            rxData: entry.data.map((consumption) => ({
              time: consumption.interval, // Keep the "YYYY-MM-DD" format
              value: consumption.rx, // rxBytes for the line
            })),
            txData: entry.data.map((consumption) => ({
              time: consumption.interval, // Keep the "YYYY-MM-DD" format
              value: consumption.tx, // txBytes for the line
            })),
          };
        });
        setApDataUsage(formattedData);
      }
    } catch (error) {
      console.error("Error fetching AP data usage:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceUpDownData = async (deviceId) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/http/health/${deviceId}`);
      if (!response?.length) return;

      // 1️⃣ Convert API data → timestamp-based
      const chartData = response.map((entry) => {
        const time = moment(entry.updatedAt);
        return {
          time: time.valueOf(), // ✅ timestamp
          Online: entry.devHealth === 1 ? 1 : 0,
          Offline: entry.devHealth === 0 ? 1 : 0,
        };
      });

      // 2️⃣ Sort safely
      chartData.sort((a, b) => a.time - b.time);

      // 3️⃣ Generate 30-minute ticks
      const startTime = moment(chartData[0].time); // ✅ exact API start
      const endTime = moment(chartData[chartData.length - 1].time);

      const ticks = [];
      let tickCursor = startTime
        .clone()
        .minute(Math.floor(startTime.minute() / 30) * 30)
        .second(0)
        .millisecond(0);

      while (tickCursor.isSameOrBefore(endTime)) {
        ticks.push(tickCursor.valueOf());
        tickCursor.add(30, "minutes");
      }
      setUpDownData(chartData);
      setXAxisLabels(ticks);
    } catch (error) {
      console.error("Error fetching device up/down data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedClients = async () => {
    setLoading(true);
    try {
      const api = new allApi();
      const serialNumber = deviceDetails?.serialNumber;
      const response = await api.getRequest(
        `api/client/switchConnectedClients?serialNumber=${serialNumber}`
      );
      if (response.response_data && response.response_data.length > 0) {
        // Map the relevant data from the response
        const formattedClients = response.response_data.map((client) => ({
          portId: client.portId,
          macAddress: client.portMacAddress,
          trafficRx: client.trafficRx,
          trafficTx: client.trafficTx,
          speed: client.speed,
        }));
        setConnectedClients(formattedClients);
      } else {
        setConnectedClients([]);
      }
    } catch (error) {
      console.error("Error fetching connected clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const getConnectedClientPerSSID = async (connectedClientsDuration) => {
    try {
      setLoading(true);
      const api = new allApi();

      // Fetch data with the specified duration
      const response = await api.getRequest(
        `api/wireless/connectedClientsPerSsid?commandType=2&duration=${connectedClientsDuration}&networkId=6001&deviceId=${deviceId}`
      );

      if (response.status === "OK") {
        const data = response.response;

        const seriesData = data.map((item) => ({
          name: item.ssidName,
          data: item.data.map((client) => {
            // Parse timestamp based on `interval1`
            const date = new Date(client.interval1);

            // Round time based on the duration
            if (
              connectedClientsDuration === "8h" ||
              connectedClientsDuration === "24h"
            ) {
              date.setMinutes(0, 0, 0); // Round to the start of the hour
            } else if (
              connectedClientsDuration === "7d" ||
              connectedClientsDuration === "30d"
            ) {
              date.setHours(0, 0, 0, 0); // Round to the start of the day
            }

            return {
              x: date.getTime(),
              y: client.totalClientSum,
            };
          }),
        }));

        // Extract unique rounded timestamps for the x-axis categories
        const timeStamps = [
          ...new Set(
            seriesData.flatMap((series) => series.data.map((point) => point.x))
          ),
        ];

        setConnectedClientCategories(timeStamps);
        setConnectedClientSeries(seriesData);
      } else {
        console.error("Failed to fetch data:", response.message);
      }
    } catch (error) {
      console.error("Error fetching connected client data:", error);
    } finally {
      setLoading(false);
    }
  };

  const connectedClientsOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: { show: false },
    },
    xaxis: {
      type: "datetime",
      categories: ConnectedClientCategories,
      labels: {
        formatter: (value) => {
          const date = new Date(value);

          // Format based on the duration selected
          if (
            connectedClientsDuration === "7d" ||
            connectedClientsDuration === "30d"
          ) {
            return date.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            }); // DD MMM format
          } else {
            return date.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
          }
        },
        style: {
          colors: "#9aa0ac",
          fontSize: "12px",
        },
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      title: { text: "Client Count" },
      labels: { formatter: (val) => val.toFixed(0) },
    },
    stroke: { width: 1 },
    dataLabels: { enabled: false },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      showForSingleSeries: true,
      labels: {
        colors: "#9aa0ac",
        fontSize: "12px",
      },
    },
    zoom: { enabled: false },
  };

  const handleConnectedClientsDurationChange = (newDuration) => {
    setConnectedClientsDuration(newDuration);
  };

  const generateColorRanges = (data) => {
    let colorRanges = [];
    let lastState = data[0].status;
    let from = data[0].updatedAt;

    data.forEach((entry, index) => {
      if (entry.status !== lastState || index === data.length - 1) {
        colorRanges.push({
          from: from,
          to: entry.updatedAt,
          color: lastState === "online" ? "#00D76F" : "#fc3858",
        });
        from = entry.updatedAt;
        lastState = entry.status;
      }
    });

    setColorRanges(colorRanges);
  };

  const handleIntervalChange = (interval) => {
    setSelectedInterval(interval);
    handleDataUsage(deviceId, interval);
  };

  const handleAPIntervalChange = (interval) => {
    setApInterval(interval);
  };

  // const connectedClientsOptions = {
  //   chart: {
  //     type: "line",
  //     height: 350,
  //     toolbar: {
  //       show: false,
  //     },
  //   },
  //   xaxis: {
  //     categories: connectedClientCategories || [], // Ensure it is always an array
  //     labels: {
  //       formatter: (value) => {
  //         if (!value) return "";
  //         const date = new Date(value);
  //         if (isNaN(date)) return value; // Handle invalid dates
  //         return date.toLocaleDateString("en-IN", {
  //           day: "2-digit",
  //           month: "short",
  //         });
  //       },
  //       style: {
  //         colors: "#9aa0ac",
  //         fontSize: "12px",
  //       },
  //     },
  //     axisBorder: {
  //       show: true,
  //       color: "#d6d6d6",
  //     },
  //     axisTicks: {
  //       show: true,
  //       color: "#d6d6d6",
  //     },
  //   },
  //   yaxis: {
  //     min: 0,
  //     labels: {
  //       formatter: (val) => (val !== undefined ? val.toFixed(0) : "N/A"),
  //     },
  //     title: {
  //       text: "Total Clients",
  //     },
  //   },
  //   stroke: {
  //     width: 2,
  //     curve: "smooth",
  //   },
  //   dataLabels: {
  //     enabled: false,
  //   },
  //   legend: {
  //     position: "bottom",
  //     horizontalAlign: "center",
  //   },
  //   colors: ConnectedClientSeries.map((_, index) =>
  //     index === 0 ? "#fc7594" : "#00aaff"
  //   ),
  //   zoom: {
  //     enabled: false,
  //   },
  // };

  return (
    <div>
      <div className="row mb-4">
        <div className="col-lg-12">
          <div className="inc-card">
            <div className="inc-card-body">
              <div className="dashboard-device-info">
                {deviceDetails && (
                  <div className="dashboard-device-icon">
                    <img src={deviceImage} alt="" />
                    <strong>{deviceDetails?.deviceName || "N/A"}</strong>
                  </div>
                )}

                <div className="dashboard-device-detail">
                  <div className="detail-list">
                    <div>
                      <strong>{deviceDetails?.serialNumber || "N/A"}</strong>
                      <span>{localizationStrings?.serialNumber}</span>
                    </div>
                    <div>
                      <strong>{deviceDetails?.ipAddress || "N/A"}</strong>
                      <span>{localizationStrings?.ipAddress}</span>
                    </div>
                    <div>
                      <strong>{deviceDetails?.macAddress || "N/A"}</strong>
                      <span>{localizationStrings?.macAddress}</span>
                    </div>
                    <div>
                      <strong>{deviceDetails?.firmwareVersion || "N/A"}</strong>
                      <span>{localizationStrings?.firmwareVersion}</span>
                    </div>
                  </div>
                  <div className="detail-list">
                    <div>
                      <strong>{usageDetails?.cpuUsage || "N/A"}</strong>{" "}
                      <span>{localizationStrings?.cpuUtilization}</span>
                    </div>
                    <div>
                      <strong>{usageDetails?.memoryUsage || "N/A"}</strong>{" "}
                      <span>{localizationStrings?.memoryUtilization}</span>
                    </div>
                    <div>
                      <strong>{deviceDetails && deviceDetails.health}</strong>
                      <span>{localizationStrings?.deviceHealth}</span>
                    </div>
                  </div>
                  <div>
                    <strong>{localizationStrings?.upDownTime}</strong>
                    <ResponsiveContainer width="98%" height={70}>
                      <BarChart data={upDownData}>
                        <XAxis
                          dataKey="time"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          ticks={xAxisLabels}
                          interval={0}
                          padding={{ left: 3, right: 0 }}
                          tickFormatter={(value) =>
                            moment(value).format("HH:mm")
                          }
                          tick={{ fontSize: 10 }}
                          barSize={2}
                        />

                        <YAxis hide />

                        <Tooltip
                          labelFormatter={(value) =>
                            moment(value).format("HH:mm")
                          }
                        />
                        <Legend />
                        <Bar
                          dataKey="Online"
                          stackId="a"
                          fill="#28a745"
                          barSize={3}
                        />
                        <Bar
                          dataKey="Offline"
                          stackId="a"
                          fill="#dc3545"
                          barSize={3}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Usage for Switch*/}
      {(deviceType === "SW" || deviceType === "Switch") && (
        <div className="row mb-4">
          <div className="col-lg-6">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.dataUsage}</h3>
                <span
                  className="dropdown"
                  id="trendDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {selectedInterval}{" "}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby="trendDropdown"
                  >
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={() => handleIntervalChange("6h")}
                      >
                        6 {localizationStrings?.hours}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={() => handleIntervalChange("12h")}
                      >
                        12 {localizationStrings?.hours}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={() => handleIntervalChange("24h")}
                      >
                        24 {localizationStrings?.hours}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={() => handleIntervalChange("7d")}
                      >
                        7 {localizationStrings?.days}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        href="#"
                        onClick={() => handleIntervalChange("30d")}
                      >
                        30 {localizationStrings?.days}
                      </a>
                    </li>
                  </ul>
                </span>
              </div>
              <div className="inc-card-body">
                {loading ? (
                  <Loader />
                ) : dataUsage.length === 0 ? (
                  <div className="no-data-box">
                    {localizationStrings?.noData}
                  </div>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        data={dataUsage}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          interval="preserveStartEnd"
                          minTickGap={20}
                          tickFormatter={(value) =>
                            moment(value).format(
                              ["6h", "12h", "24h"].includes(selectedInterval)
                                ? "HH:mm"
                                : "DD MMM"
                            )
                          }
                          tick={{ fill: "#9aa0ac", fontSize: 12 }}
                        />
                        <YAxis
                          tickFormatter={(value) => `${value} MB`}
                          tick={{ fill: "#9aa0ac", fontSize: "12px" }}
                        />
                        <Tooltip formatter={(value) => [`${value} MB`]} />
                        <Legend
                          formatter={() =>
                            `${deviceDetails?.serialNumber || "N/A"}`
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="totalTraffic"
                          stroke="#4791FF"
                          name="Total Traffic"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    {/* <div className="data-label">
                      <span className="download">
                        <i></i>Download
                      </span>
                      <span className="upload">
                        <i></i>Upload
                      </span>
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.connectedClients}</h3>
              </div>
              <div className="inc-card-body">
                {loading ? (
                  <Loader />
                ) : (
                  <div className="inc-card-table">
                    <table className="full-table">
                      <thead>
                        <tr>
                          <th>{localizationStrings?.port}</th>
                          <th>{localizationStrings?.macAddress}</th>
                          <th>{localizationStrings?.traffic} RX</th>
                          <th>{localizationStrings?.traffic} TX</th>
                          <th>{localizationStrings?.speed} (Mbps)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {connectedClients.length > 0 ? (
                          connectedClients.map((client) => (
                            <tr key={client.portId}>
                              <td>{client.portId}</td>
                              <td>{client.macAddress}</td>
                              <td>{client.trafficRx}</td>
                              <td>{client.trafficTx}</td>
                              <td>{client.speed}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">
                              {localizationStrings?.noConnectedClientsFound}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Usage for AP*/}
      {deviceType === "AP" && (
        <div className="row mb-4">
          <div className="col-lg-7">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.clientDataUsage}</h3>
                <span
                  className="dropdown"
                  id="trendDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {apInterval}{" "}
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                  <ul
                    className="dropdown-menu dropdown-menu-end"
                    aria-labelledby="trendDropdown"
                  >
                    {["8h", "24h", "7d", "30d"].map((interval) => (
                      <li key={interval}>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={() => handleAPIntervalChange(interval)}
                        >
                          {interval}
                        </a>
                      </li>
                    ))}
                  </ul>
                </span>
              </div>
              <div className="inc-card-body">
                {loading ? (
                  <Loader />
                ) : apDataUsage.length === 0 ? (
                  <div className="no-data-box">
                    {localizationStrings?.noData}
                  </div>
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="time"
                          type="category"
                          allowDuplicatedCategory={false}
                        />
                        <YAxis tickFormatter={(value) => `${value}`} />
                        <Tooltip
                          formatter={(value, name, props) => [`${value}`, name]}
                        />
                        <Legend />

                        {/* Loop over each device to create a line for rxBytes and txBytes */}
                        {apDataUsage.map((device) => (
                          <React.Fragment key={device.serialNumber}>
                            <Line
                              type="monotone"
                              dataKey="value"
                              data={device.rxData} // Data for rxBytes
                              stroke="#4791FF"
                              name={`Download (${device.serialNumber})`} // Use serialNumber in the legend
                              strokeWidth={1}
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              data={device.txData} // Data for txBytes
                              stroke="#FF9050"
                              name={`Upload (${device.serialNumber})`} // Use serialNumber in the legend
                              strokeWidth={1}
                              dot={false}
                            />
                          </React.Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="inc-card">
              <div className="inc-card-title">
                <h3>{localizationStrings?.connectedClientsPerSSID}</h3>
                <div className="card-title-dropdown">
                  <span className="dropdown" data-bs-toggle="dropdown">
                    <span>
                      {connectedClientsDuration === "24h"
                        ? "24h"
                        : connectedClientsDuration}
                      <i className="fa fa-angle-down" aria-hidden="true"></i>
                    </span>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            handleConnectedClientsDurationChange("8h")
                          }
                        >
                          {localizationStrings?.last8Hours}
                        </a>
                      </li>
                      <li>
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            handleConnectedClientsDurationChange("24h")
                          }
                        >
                          {localizationStrings?.last24Hours}
                        </a>
                      </li>
                      <li>
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            handleConnectedClientsDurationChange("7d")
                          }
                        >
                          {localizationStrings?.last7Days}
                        </a>
                      </li>
                      <li>
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            handleConnectedClientsDurationChange("30d")
                          }
                        >
                          {localizationStrings?.last30Days}
                        </a>
                      </li>
                    </ul>
                  </span>
                </div>
              </div>
              <div className="inc-card-body">
                {loading ? (
                  <Loader />
                ) : (
                  <div>
                    {ConnectedClientSeries.length === 0 ? (
                      <div className="no-data-box">
                        <div className="no-data-text">
                          {localizationStrings?.noData}
                        </div>
                      </div>
                    ) : (
                      <Chart
                        options={connectedClientsOptions}
                        series={ConnectedClientSeries}
                        type="line"
                        height={170}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-lg-12">
          <div className="inc-card">
            <div className="inc-card-title">
              <h3>{localizationStrings?.deviceLocation}</h3>
            </div>
            <div className="inc-card-body">
              <div>
                <MapContainer
                  center={deviceLocation}
                  zoom={12}
                  style={{ height: "200px", width: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapView center={deviceLocation} />{" "}
                  {/* Automatically updates the center */}
                  {nodes.map((node) => (
                    <Marker
                      key={node.id}
                      position={node.position}
                      icon={node.icon}
                    />
                  ))}
                </MapContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeviceDashboard;
